import { cacheSet, cloneObject, error, getAssetKey, validateObject } from "./utils";
import { magicSdkBucket } from "./globals";

export default class MagicSdkWrapper {
    constructor() {
        this.magicSDK = null;
        this.photosAdded = false;
        this.photos = [];
        this.size = "small";
    }

    /**
     * Return the magicSdk url from aws
     * @param env
     * @returns {string}
     */
    magicSDKUrl(env) {
        return `${magicSdkBucket}/${env}/v1/magic-sdk.js`;
    }

    /**
     * Validate that the item has all necessary params
     * @param item
     * @returns {boolean}
     */
    validateItem(item) {
        if (!Array.isArray(item.selectedPhotos)) {
            error("Validation Error", "selectedPhotos is not an array");
        } else {
            if (item.selectedPhotos.length > 0) {
                item.selectedPhotos.forEach((photo, index) => {
                    if (typeof photo !== "object") {
                        error("Validation Error", `photo at index ${index} is not an object`);
                    } else {
                        validateObject(`selectedPhotos - at index ${index}`, photo, ["id"])
                    }
                })
            }
        }

        if (!item.sizeId) {
            error("Validation Error", "sizeId is not a number");
        }

        if (!item.occasionId) {
            error("Validation Error", "occasionId is not a number");
        }

        if (!item.styleId) {
            error("Validation Error", "styleId is not a number");
        }

        if (!item.brand) {
            error("Validation Error", "brand is empty");
        }

        if (typeof item.addons !== "object" || item.addons === "") {
            error("Validation Error", "addons is not an object");
        }

        if (typeof item.project === "object") {
            if (item.designJson || item.layout && isNaN(item.sizeId) || isNaN(item.styleId) || isNaN(item.occasionId)) {
                if (typeof item.designJson !== "object" || item.designJson === "") {
                    error("Validation Error", "designJson is not an object");
                }
                if (typeof item.layout !== "object" || item.layout === "") {
                    error("Validation Error", "layout is not an object");
                }
            }

        }

        return true;
    }


    /**
     * Inner method for adding the photos to the magic sdk
     * @private
     * @memberOf MagicRenderer
     */
    _addPhotos(userId, token, photos = []) {
        if (photos.length > 0) {
            const addPhotosParams = {
                userId: userId,
                photos,
                oauthToken: token,
                migrated: true,
                timeout: 15000,
                appendAOI: true
            };

            this.magicSDK.addPhotos(addPhotosParams).then(() => {
                this.photosAdded = true;
            }).catch(() => {
                this.photosAdded = true;
            });
        }
    }

    /**
     * Handle the response from getLayouts call and store the results in cache by certain params
     * @param resultItems
     * @param items
     */
    handleGetLayoutsResult(resultItems, items) {
        const returnedKeys = Object.keys(resultItems);

        if (returnedKeys.length > 0) {
            for (const key of returnedKeys) {

                items.some(item => {
                    const splittedKey = key.split("-");
                    const [brand, sizeId, occasionId, styleId] = (!isNaN(splittedKey[0]) ? ["SFLY", ...splittedKey] : splittedKey);

                    if (brand === item.brand && parseInt(item.sizeId) === parseInt(sizeId) && parseInt(item.occasionId) === parseInt(occasionId) && parseInt(item.styleId) === parseInt(styleId) && (item.layoutName === null || item.layoutName === resultItems[key].name)) {
                        item.layoutData = cloneObject(resultItems[key]);
                        const assetKey = getAssetKey(item);
                        cacheSet(assetKey, resultItems[key]);

                        return true;
                    }
                });

            }

        }
    }

    /**
     * Get layout of one photo, according to uploaded photo orientation
     * @param resultItem
     * @param item
     */
    getOnePhotoLayout(resultItem, item) {
        const isLandscapeItemImage = item.selectedPhotos[0].origHeight < item.selectedPhotos[0].origWidth;
        const onePhotoLayouts = resultItem.surfaces[0].layoutList?.layouts?.filter(currentLayout => Number(currentLayout.numPhotos) === 1);
        let layout = onePhotoLayouts?.find(currentLayout => (currentLayout.imageAreas[0].orientation.toLowerCase() === "landscape") === isLandscapeItemImage);
        let allowCache = true;
        if (layout) {
            if (onePhotoLayouts.length > 1) {
                allowCache = false;
            }
        } else if (onePhotoLayouts.length > 0) {
            layout = onePhotoLayouts[0];
        }

        return { layout, allowCache };
    }

    /**
     * Handle the response from blueprint call and store the results in cache by certain params
     * @param resultItem
     * @param items
     */
    handleGetBpLayoutsResult(resultItem, items, url) {
        const item = items.find(item => url.includes(item.sizeId) && url.includes(item.occasionId) && url.includes(item.styleId) && url.includes(item.brand));
        if (item) {
            let layout;
            let allowCache = true;
            if (item.filters && item.filters['numberOfPhotos'] && item.filters['numberOfPhotos'].length > 0) {
                if (item.filters['numberOfPhotos'].length === 1 && Number(item.filters['numberOfPhotos'][0]) === 1) {
                    const result = this.getOnePhotoLayout(resultItem, item);
                    layout = result.layout;
                    allowCache = result.allowCache;
                } else {
                    for (let i = 0; i < item.filters['numberOfPhotos'].length; i++) {
                        const numberOfPhotos = item.filters['numberOfPhotos'][i];
                        if (typeof numberOfPhotos === 'string' && numberOfPhotos.includes('5')) {
                            layout = resultItem.surfaces[0].layoutList?.layouts?.find(currentLayout => Number(currentLayout.numPhotos) >= 5);
                        } else {
                            layout = resultItem.surfaces[0].layoutList?.layouts?.find(currentLayout => Number(currentLayout.numPhotos) === Number(numberOfPhotos));
                        }
                        if (layout) {
                            break;
                        }
                    }
                }
            } else {
                if (item.selectedPhotos && item.selectedPhotos.length === 1) {
                    const result = this.getOnePhotoLayout(resultItem, item);
                    layout = result.layout;
                    allowCache = result.allowCache;
                } else if (item.selectedPhotos && item.selectedPhotos.length > 1) {
                    layout = resultItem.surfaces[0].layoutList?.layouts?.find(currentLayout => Number(currentLayout.numPhotos) === item.selectedPhotos.length);
                }
                if (!layout) {
                    const defaultLayoutId = resultItem.surfaces[0].defaults?.layoutId;
                    layout = resultItem.surfaces[0].layoutList?.layouts?.find(currentLayout => currentLayout.id === defaultLayoutId);
                    if (!layout) {
                        const layouts = resultItem.surfaces[0].layoutList?.layouts;
                        if (layouts && layouts.length > 0) {
                            layout = layouts[0];
                        }
                    }
                }
            }

            if (layout) {
                layout.transparentThumbUrl = layout.transparentThumbUrl.replace('trans.text.png', 'trans.png');
                if (this.size === 'medium') {
                    layout.transparentThumbUrl = layout.transparentThumbUrl.replace('png.280', 'png.600');
                }
                item.layoutData = cloneObject(layout);
                if ((!item.selectedPhotos || item.selectedPhotos.length === 0) && item.layoutData.numPhotos === 1) {
                    const onePhotoLayouts = resultItem.surfaces[0].layoutList?.layouts?.filter(currentLayout => Number(currentLayout.numPhotos) === 1);
                    if (onePhotoLayouts.length > 1) {
                        allowCache = false;
                    }
                }
                if (allowCache) {
                    const assetKey = getAssetKey(item, item.isMlsdk, item.layoutData.numPhotos);
                    cacheSet(assetKey, layout);
                }
            }
        }
    }

    /**
     * Method that being called from the MagicRenderer.generate, will trigger magicSDK.fitPhotosToLayouts
     * @private
     * @memberOf MagicRenderer
     */
    async fitPhotosToLayout(item, userId, token) {
        const fitParams = {
            userId: userId,
            photos: item.selectedPhotos.length > 0 ? item.selectedPhotos : item.photos,
            oauthToken: token,
            migrated: true,
            timeout: 10000,
            layouts: [this.layoutDataToMGLayout(item)],
            changeGroups: true,
            forceFit: true,
            force: true,
            onlySmarts: false,
            forceGroups: true
        };

        if (item.hasTextData) fitParams.userData = item.textData;

        try {
            if (Array.isArray(fitParams.photos) && fitParams.photos.length > 0) {
                return await this.magicSDK.fitPhotosToLayouts(fitParams);
            } else {
                return null;
            }
        } catch (error) {
            throw error;
        }

    };

    /**
     * Converts the layout data (from blueprint) into mg layout style for fitPhotosToLayouts
     * @private
     * @memberOf MagicRenderer
     */
    layoutDataToMGLayout(item = {}) {
        const layout = {
            id: item.layoutData.id,
            wells: [],
            width: item.layoutData.canvasWidth,
            height: item.layoutData.canvasHeight,
            useMask: false,
            isLandscape: false,
            default: true,
            bgColor: "#FFFFFF",
            layoutName: item.layoutData.name,
            group: 1
        };

        try {
            item.wells = (item.layoutData.imageAreas || [])
                .concat(item.layoutData.textAreas.map(area => Object.assign(area, { t: true })) || [])
                .sort(area => area.id)

            const imagesLayValue = [];
            const notImagesLayValue = [];
            item.wells.forEach(value => {
                if (!value.b && !value.t && !value.e) {
                    imagesLayValue.push(value);
                } else {
                    notImagesLayValue.push(value);
                }
            });
            item.wells = imagesLayValue.concat(notImagesLayValue);

            layout.wells = item.wells.map((well, index) => {
                const newWell = {
                    wellIndex: index,
                    type: well.t ? "text" : "image",
                    width: (well.width / item.layoutData.canvasWidth) * 100,
                    height: (well.height / item.layoutData.canvasHeight) * 100,
                    x: well.x,
                    y: well.y
                };

                if (well.t) {
                    newWell.textArea = {
                        id: well.id,
                        templateText: "",
                        fieldsUsed: [],
                        font: {
                            id: "body",
                            color: "#000000",
                            name: "LiebeErikaSF Regular",
                            pointSize: 32,
                            bold: false,
                            italic: false,
                            underline: false,
                            horizontalJustification: "center",
                            verticalJustification: "middle"
                        }
                    };

                    // Add template
                    if (typeof item.layoutData.textAreasTemplates === "object" && item.layoutData.textAreasTemplates[well.id]?.templateText !== null) {
                        newWell.textArea.templateText =
                            item.layoutData.textAreasTemplates[well.id].templateText;
                        newWell.textArea.fieldsUsed =
                            item.layoutData.textAreasTemplates[well.id].fieldsUsed;
                    }

                }

                return newWell;
            });

        } catch (err) {
            error("Error", `failed layoutDataToMGLayout - ${err.message}`);
        }

        return layout;
    };


    /**
     * Get the surfaceWrapper params from item and fitData
     * @param item
     * @param fitData
     * @param extra
     * @returns {{sizeId: *, photoAssignment: ({photoId: *, imageAreaId: *, crop: *}[]|*[]), styleId: *, hasTextData: boolean, occasionId: *, photos: (*|*[]), layoutName: *}}
     */
    getSurfaceWrapperParams(item, fitData, extra = {}) {
        return Object.assign({
            hasTextData: item.hasTextData,
            sizeId: item.sizeId,
            styleId: item.styleId,
            occasionId: item.occasionId,
            layoutName: item.layoutData?.name,
            photos: typeof fitData === "object" && fitData !== null ? fitData.photoStrip.map(photo => ({
                id: photo.photoId,
                url: photo.url
            })) : [],
            photoAssignment: typeof fitData === "object" && fitData !== null && fitData.layouts && fitData.layouts.length !== 0 ? fitData.layouts[0]?.photos.filter(photo => photo !== null).map(
                photo => ({
                    photoId: photo.photoId,
                    crop: photo.crop,
                    imageAreaId: item.layoutData && Object.keys(item.layoutData).length > 0 ? item.layoutData?.imageAreas[photo.wellIndex]?.id : photo.wellIndex
                })) : [],
            item: item
        }, extra);
    };

    /**
     * Create projectJson
     * @param layoutWithPhotos
     * @param params
     * @param userId
     * @returns {{photoStrip: *, surfaces: [*], info: {sizeId: *, productCode: *, addons: *, styleId: *, occasionId: *, sku: *, userId: *, brand: *, productType: string}}}
     */
    createProjectJson(layoutWithPhotos, params, userId) {
        layoutWithPhotos.photoStrip.forEach((photo, index) => {
            photo.photoRefId = index + 1
        });
        const layout = layoutWithPhotos.hasOwnProperty('layouts') ? layoutWithPhotos.layouts[0] : {};
        let layoutId = params.layoutName;
        const surfaces = [this.generateLayoutSurface(layout, layoutId, 'front', layoutWithPhotos.photoStrip)];

        const product = {
            surfaces: surfaces,
            photoStrip: layoutWithPhotos.photoStrip,
            info: {
                occasionId: params.occasionId,
                styleId: params.styleId,
                sizeId: params.sizeId,
                userId,
                productType: "",
                productCode: params.productCode,
                sku: params.skuCode,
                addons: params.addons,
                brand: params.brand
            }
        };


        if (layoutWithPhotos.layouts.length > 1) {
            layoutWithPhotos.layouts.forEach((layout, index) => {
                if (index === 0) {
                    return;
                }
                product.surfaces.push(this.generateLayoutSurface(layout, layoutId, `front${(index + 1)}`, layoutWithPhotos.photoStrip));
            });
        }


        return product;
    }


    /**
     * Generate the layout surface for the projectJson
     * @param layout
     * @param layoutId
     * @param type
     * @param photoStrip
     * @returns {{canvas: {layout: {textareas: ([]|*[]), layoutId: (string|*), photos: *}}, type: *}}
     */
    generateLayoutSurface(layout, layoutId, type, photoStrip) {

        const hasMultiSurface = layoutId.indexOf('|surface');
        const prodStyle = hasMultiSurface !== -1 ? layoutId.substring(0, hasMultiSurface) : layoutId;

        // sort photos by well index but actually we need to this in the thumbgen
        layout.photos.sort((a, b) => a.wellIndex < b.wellIndex ? -1 : 1);
        try {
            return {
                type,
                canvas: {
                    layout: {
                        layoutId: prodStyle,
                        photos: layout.photos.map(photo => {
                            let photoRefId;
                            for (let i = 0; i < photoStrip.length; i++) {
                                if (photoStrip[i].photoId === photo.photoId) {
                                    photoRefId = i + 1;
                                    break;
                                }
                            }
                            return {
                                finalCrop: photo.crop,
                                photoRefId: photoRefId,
                                seqNum: photo.wellIndex
                            }
                        }),
                        textareas: layout.hasOwnProperty('textWells') ? layout.textWells : []
                    }
                }
            }
        } catch (e) {
            error("generateLayoutSurface", `failed to create project - ${e}`);
        }
    }
}





